// hooks/useGuestTimer.js - Guest timer management logic
// Extracted from PlatformSelector.js and Dashboard.js

import { useState, useEffect, useCallback } from 'react';
import logger from '../services/logger';

/**
 * Custom hook for managing guest session timer
 */
export const useGuestTimer = (navigate, onSessionExpired) => {
  // Guest session state
  const [isGuest, setIsGuest] = useState(false);
  const [guestTimeRemaining, setGuestTimeRemaining] = useState(0);
  const [guestSessionStart, setGuestSessionStart] = useState(null);

  /**
   * Format time in MM:SS format
   */
  const formatTime = useCallback((ms) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  /**
   * Handle guest session initialization and validation
   */
  const handleGuestSession = useCallback(() => {
    const sessionStart = localStorage.getItem('guestSession');
    
    if (sessionStart) {
      const startTime = parseInt(sessionStart);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 15 * 60 * 1000 - elapsed); // 15 minutes in ms
      
      if (remaining > 0) {
        setIsGuest(true);
        setGuestTimeRemaining(remaining);
        setGuestSessionStart(startTime);
        
        return remaining;
      } else {
        // Session expired
        if (onSessionExpired) {
          onSessionExpired(navigate);
        }
        return 0;
      }
    } else {
      // No session, redirect to auth
      if (navigate) {
        navigate('/');
      }
      return 0;
    }
  }, [navigate, onSessionExpired]);

  /**
   * Initialize guest timer with countdown
   */
  const initializeGuestTimer = useCallback(() => {
    const remaining = handleGuestSession();
    
    if (remaining > 0 && guestSessionStart) {
      // Set up countdown timer
      const timer = setInterval(() => {
        const newElapsed = Date.now() - guestSessionStart;
        const newRemaining = Math.max(0, 15 * 60 * 1000 - newElapsed);
        
        if (newRemaining === 0) {
          clearInterval(timer);
          if (onSessionExpired) {
            onSessionExpired(navigate);
          }
        } else {
          setGuestTimeRemaining(newRemaining);
        }
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [guestSessionStart, handleGuestSession, navigate, onSessionExpired]);

  /**
   * Check guest session validity
   */
  const checkGuestSession = useCallback(() => {
    const sessionStart = localStorage.getItem('guestSession');
    
    if (sessionStart) {
      const startTime = parseInt(sessionStart);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 15 * 60 * 1000 - elapsed);
      
      return {
        isValid: remaining > 0,
        remaining,
        elapsed,
        startTime
      };
    }
    
    return {
      isValid: false,
      remaining: 0,
      elapsed: 0,
      startTime: null
    };
  }, []);

  /**
   * Extended guest session timer for dashboard
   */
  const initializeDashboardTimer = useCallback((setUIState) => {
    const sessionData = checkGuestSession();
    
    if (sessionData.isValid) {
      setIsGuest(true);
      setGuestTimeRemaining(sessionData.remaining);
      setGuestSessionStart(sessionData.startTime);
      
      // Set up countdown timer with dashboard-specific handling
      const timer = setInterval(() => {
        const newSessionData = checkGuestSession();
        
        if (!newSessionData.isValid) {
          clearInterval(timer);
          if (onSessionExpired) {
            onSessionExpired(navigate, setUIState);
          }
        } else {
          setGuestTimeRemaining(newSessionData.remaining);
        }
      }, 1000);
      
      return () => clearInterval(timer);
    } else if (onSessionExpired) {
      onSessionExpired(navigate, setUIState);
    }
  }, [checkGuestSession, navigate, onSessionExpired]);

  /**
   * Reset guest session
   */
  const resetGuestSession = useCallback(() => {
    setIsGuest(false);
    setGuestTimeRemaining(0);
    setGuestSessionStart(null);
    localStorage.removeItem('guestSession');
  }, []);

  /**
   * Extend guest session (if allowed)
   */
  const extendGuestSession = useCallback((additionalMinutes = 5) => {
    const newStartTime = Date.now() - (15 * 60 * 1000 - additionalMinutes * 60 * 1000);
    localStorage.setItem('guestSession', newStartTime.toString());
    setGuestSessionStart(newStartTime);
    
    const newRemaining = additionalMinutes * 60 * 1000;
    setGuestTimeRemaining(newRemaining);
    
    logger.success(`Guest session extended by ${additionalMinutes} minutes`);
  }, []);

  /**
   * Get time remaining in different formats
   */
  const getTimeRemaining = useCallback(() => {
    return {
      ms: guestTimeRemaining,
      formatted: formatTime(guestTimeRemaining),
      minutes: Math.floor(guestTimeRemaining / 60000),
      seconds: Math.floor((guestTimeRemaining % 60000) / 1000),
      percentage: guestTimeRemaining / (15 * 60 * 1000) * 100
    };
  }, [guestTimeRemaining, formatTime]);

  /**
   * Check if session is about to expire (less than 2 minutes)
   */
  const isSessionAboutToExpire = useCallback(() => {
    return guestTimeRemaining < 2 * 60 * 1000 && guestTimeRemaining > 0;
  }, [guestTimeRemaining]);

  /**
   * Check if session is in critical time (less than 30 seconds)
   */
  const isSessionCritical = useCallback(() => {
    return guestTimeRemaining < 30 * 1000 && guestTimeRemaining > 0;
  }, [guestTimeRemaining]);

  // Initialize timer when component mounts
  useEffect(() => {
    let cleanup;
    
    // Check if there's an existing session
    const sessionStart = localStorage.getItem('guestSession');
    if (sessionStart) {
      cleanup = initializeGuestTimer();
    }
    
    return cleanup;
  }, [initializeGuestTimer]);

  return {
    // State
    isGuest,
    guestTimeRemaining,
    guestSessionStart,
    
    // Functions
    handleGuestSession,
    initializeGuestTimer,
    initializeDashboardTimer,
    checkGuestSession,
    resetGuestSession,
    extendGuestSession,
    formatTime,
    getTimeRemaining,
    isSessionAboutToExpire,
    isSessionCritical
  };
};